/**
 * User-scoped storage module for PrestaMass.
 * Prefixes all localStorage keys with the authenticated user's ID
 * to ensure complete data isolation between users.
 */

const USER_ID_KEY = 'pm_current_user_id';

/**
 * Set the current authenticated user ID.
 * Called after login, cleared on logout.
 */
export function setCurrentUserId(userId: string | null): void {
  if (typeof window === 'undefined') return;
  if (userId) {
    localStorage.setItem(USER_ID_KEY, userId);
  } else {
    localStorage.removeItem(USER_ID_KEY);
  }
}

/**
 * Get the current authenticated user ID.
 */
export function getCurrentUserId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(USER_ID_KEY);
}

/**
 * Get a user-scoped storage key.
 * Format: pm_<userId>_<originalKey>
 * Falls back to original key if no user is set (backward compatibility).
 */
export function scopedKey(key: string): string {
  const userId = getCurrentUserId();
  if (!userId) return key;
  return `pm_${userId}_${key.replace(/^pm_/, '')}`;
}

/**
 * Load data from user-scoped localStorage.
 */
export function loadScoped<T>(key: string): T[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(scopedKey(key));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Save data to user-scoped localStorage.
 */
export function saveScoped<T>(key: string, data: T[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(scopedKey(key), JSON.stringify(data));
}

/**
 * Load a single value from user-scoped localStorage.
 */
export function loadScopedValue(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(scopedKey(key));
}

/**
 * Save a single value to user-scoped localStorage.
 */
export function saveScopedValue(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(scopedKey(key), value);
}
